import React, { useCallback, useState } from 'react';
import { Pressable, View } from 'react-native';

import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import Reveal from '@/components/Landing/Reveal';
import { Split } from '@/components/Landing/LandingKit';
import { PricingLink, PricingSection, PricingTitle, usePricing } from './PricingKit';
import { EASE, isWeb } from '@/components/Landing/landingTokens';
import { FAQ } from '@/constants/pricingContent';

/**
 * One question.
 *
 * Rows are separated by hairlines rather than boxed as cards: ruled rows keep the
 * answers easy to scan without adding another layer of cards.
 */
function FaqRow({ item, isOpen, onToggle }) {
  const { c } = usePricing();

  return (
    <View style={{ borderBottomWidth: 1, borderBottomColor: c.rule }}>
      <Pressable
        onPress={onToggle}
        accessibilityRole="button"
        accessibilityState={{ expanded: isOpen }}
        {...(isWeb ? { 'aria-expanded': isOpen } : {})}
        accessibilityLabel={item.question}
        style={({ hovered }) => ({
          flexDirection: 'row',
          alignItems: 'center',
          gap: 18,
          paddingVertical: 24,
          opacity: hovered ? 0.75 : 1,
          ...(isWeb ? { cursor: 'pointer', transition: 'opacity 160ms linear' } : {}),
        })}
      >
        <Text
          weight="medium"
          style={{ color: c.ink, fontSize: 17, lineHeight: 25, letterSpacing: -0.2, flex: 1, minWidth: 0 }}
        >
          {item.question}
        </Text>
        <View
          aria-hidden
          style={{
            width: 26,
            height: 26,
            borderRadius: 8,
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'transparent',
            ...(isWeb ? {
              transform: [{ rotate: isOpen ? '180deg' : '0deg' }],
              transition: `transform 300ms ${EASE}, background-color 220ms linear`,
            } : {}),
          }}
        >
          <Icon name="chevron-down" size={11} color={isOpen ? c.accent : c.muted} solid />
        </View>
      </Pressable>

      {isOpen ? (
        <Text style={{ color: c.muted, fontSize: 15, lineHeight: 25, paddingBottom: 22, maxWidth: 620 }}>
          {item.answer}
        </Text>
      ) : null}
    </View>
  );
}

function PricingFaq() {
  const { c, isCompact } = usePricing();
  const [openId, setOpenId] = useState(null);

  // Keep one answer open at a time so the questions stay easy to scan.
  const toggle = useCallback((question) => {
    setOpenId((current) => (current === question ? null : question));
  }, []);

  return (
    <PricingSection label="Pricing questions">
      <Split ratio={[1, 1.5]} align="flex-start">
        <View style={{ gap: 18 }}>
          <Text weight="medium" style={{ color: c.muted, fontSize: 14 }}>Questions</Text>
          <PricingTitle>A few things to know.</PricingTitle>
          <PricingLink label="Talk to us" href="/contact" />
        </View>

        <Reveal delay={120}>
          <View style={{ borderTopWidth: 1, borderTopColor: c.rule, paddingTop: isCompact ? 0 : 4 }}>
            {FAQ.map((item) => (
              <FaqRow
                key={item.question}
                item={item}
                isOpen={openId === item.question}
                onToggle={() => toggle(item.question)}
              />
            ))}
          </View>
        </Reveal>
      </Split>
    </PricingSection>
  );
}

export default React.memo(PricingFaq);
